import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import {
  EditorialFacebookPostNetwork,
  EditorialFacebookPostType,
} from '@/utils/enums';

const Dialog = styled(StyledDialog)`
  width: 400px;
  text-align: left;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
  text-align: center;
`;

const Footer = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  box-shadow: 0px -1px 8px 0px #2222220f;
  gap: 24px;
  margin: 32px -24px -40px;
`;

const FooterButton = styled(Button)`
  flex: 1;
`;

type FormDataT = {
  date_from: string;
  networks: number[];
  post_types: number[];
  date_until: string;
  pages: number[];
};

const networks = [
  {
    value: EditorialFacebookPostNetwork.FACEBOOK,
    label: 'Facebook',
  },
  {
    value: EditorialFacebookPostNetwork.INSTAGRAM,
    label: 'Instagram',
  },
  {
    value: EditorialFacebookPostNetwork.PINTEREST,
    label: 'Pinterest',
  },
  {
    value: EditorialFacebookPostNetwork.REELS,
    label: 'Facebook Reels',
  },
  {
    value: EditorialFacebookPostNetwork.FACEBOOK_VIDEO,
    label: 'Facebook Video',
  },
  {
    value: EditorialFacebookPostNetwork.TEXT_STATUS,
    label: 'Facebook Text Status',
  },
  {
    value: EditorialFacebookPostNetwork.THREADS,
    label: 'Threads',
  },
  {
    value: EditorialFacebookPostNetwork.TWITTER,
    label: 'Twitter X',
  },
  {
    value: EditorialFacebookPostNetwork.WHATSAPP,
    label: 'WhatsApp',
  },
  {
    value: EditorialFacebookPostNetwork.BLUESKY,
    label: 'Bluesky',
  },
  {
    value: EditorialFacebookPostNetwork.YOUTUBE_SHORTS,
    label: 'Youtube Shorts',
  },
  {
    value: EditorialFacebookPostNetwork.LINKEDIN,
    label: 'LinkedIn',
  },
  {
    value: EditorialFacebookPostNetwork.TIKTOK,
    label: 'Tiktok',
  },
  {
    value: EditorialFacebookPostNetwork.MSN,
    label: 'MSN',
  },
  {
    value: EditorialFacebookPostNetwork.REDDIT,
    label: 'Reddit',
  },
];

const postTypes = [
  {
    value: EditorialFacebookPostType.IMAGE,
    label: 'Image',
  },
  {
    value: EditorialFacebookPostType.LINK,
    label: 'Link',
  },
  {
    value: EditorialFacebookPostType.SHARE,
    label: 'Share',
  },
  {
    value: EditorialFacebookPostType.PLACEHOLDER,
    label: 'Placeholder',
  },
  {
    value: EditorialFacebookPostType.REEL,
    label: 'Reel',
  },
  {
    value: EditorialFacebookPostType.STORY,
    label: 'Story',
  },
  {
    value: EditorialFacebookPostType.TEXT,
    label: 'Text',
  },
  {
    value: EditorialFacebookPostType.VIDEO,
    label: 'Video',
  },
];

interface Props {
  onClose: VoidFunction;
}

const ExportModal: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();

  const { data: pages } = useQuery(
    ['editorial_distribution_queue_channels'],
    () => api.get<never, EditorialQueuePage[]>('/v1/editorial/queue/channels'),
    {
      staleTime: 120_000,
    },
  );

  const {
    control,
    formState: { isSubmitting },
    clearErrors,
    handleSubmit,
  } = useForm<FormDataT>({
    defaultValues: {
      date_from: '',
      date_until: '',
      networks: [],
      post_types: [],
      pages: [],
    },
  });

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const params = new URLSearchParams();

        params.append('date_from', data.date_from);
        params.append('date_until', data.date_until);
        data.post_types.forEach((val) =>
          params.append('post_types[]', val.toString()),
        );
        data.networks.forEach((val) =>
          params.append('networks[]', val.toString()),
        );
        data.pages.forEach((val) => params.append('pages[]', val.toString()));

        const url = `${process.env.REACT_APP_API_ENDPOINT}/api/v1/editorial/queue/posts-export?${params.toString()}`;

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'posts.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (_) {
        alerts.error();
      }
    })();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="heading2">Export Posts</Heading>
        <FormLayout>
          <FormField<FormDataT>
            type="datepicker"
            label="Published From"
            control={control}
            name="date_from"
          />
          <FormField<FormDataT>
            type="datepicker"
            label="Published Until"
            control={control}
            name="date_until"
          />
          <FormField<FormDataT, number>
            type="multiselect"
            label="Pages"
            control={control}
            options={pages?.map((p) => ({ value: p.id, label: p.name })) ?? []}
            name="pages"
          />
          <FormField<FormDataT, number>
            type="multiselect"
            label="Networks & Queues"
            control={control}
            options={networks}
            name="networks"
          />
          <FormField<FormDataT, number>
            type="multiselect"
            label="Post Types"
            control={control}
            options={postTypes}
            name="post_types"
          />
        </FormLayout>

        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Footer>
          <FooterButton
            type="button"
            color="primary"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Export
          </FooterButton>
          <FooterButton type="button" color="secondary" onClick={onClose}>
            Cancel
          </FooterButton>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default ExportModal;
